<template>
    <div class="carousel">
        <div class="carousel-wrapper" v-if="images.length" @touchstart="handleTouchStart" @touchmove="handleTouchMove"
            @touchend="handleTouchEnd">
            <transition name="carousel-fade" mode="out-in">
                <div v-if="images[current]" :key="images[current].id" class="carousel-item" :style="dragStyle">
                    <img :src="fullImageUrl(images[current].coverImage)" :alt="images[current].projectName" />
                    <div class="carousel-caption">
                        <router-link :to="`/project/${images[current].id}`" class="carousel-title">{{
                            images[current].projectName }}</router-link>
                        <router-link :to="`/project/${images[current].id}`"
                            class="carousel-detail-btn">查看详情</router-link>
                    </div>
                </div>
            </transition>
            <div class="carousel-indicators" v-if="images.length > 1">
                <span v-for="(img, idx) in images" :key="img.id" :class="{ active: idx === current }"
                    @click="goTo(idx)"></span>
            </div>
            <button class="carousel-btn prev" @click="prev">‹</button>
            <button class="carousel-btn next" @click="next">›</button>
        </div>
        <div v-else class="carousel-placeholder"></div>
    </div>
</template>

<script>
import { getHomeCarousel } from '../api/project';
import config from '../config';

export default {
    name: 'Carousel',
    data() {
        return {
            images: [],
            current: 0,
            timer: null,
            touchStartX: 0,
            touchStartY: 0,
            touchEndX: 0,
            touchEndY: 0,
            isSwiping: false,
            dragOffset: 0, // 新增：拖拽偏移
            isDragging: false,
            autoPlayDelay: null // 新增：自动播放延迟
        };
    },
    mounted() {
        this.fetchImages();
        this.startAutoPlay();
    },
    beforeDestroy() {
        clearInterval(this.timer);
    },
    methods: {
        fetchImages() {
            getHomeCarousel().then(res => {
                if (res.data.code === 200 && Array.isArray(res.data.data)) {
                    this.images = res.data.data;
                }
            });
        },
        fullImageUrl(path) {
            if (!path) return '';
            if (path.startsWith('http')) return path;
            return config.API_BASE_URL + path;
        },
        startAutoPlay() {
            if (this.timer) clearInterval(this.timer);
            this.timer = setInterval(() => {
                this.next();
            }, 4000);
        },
        pauseAutoPlay() {
            if (this.timer) clearInterval(this.timer);
            this.timer = null;
            if (this.autoPlayDelay) clearTimeout(this.autoPlayDelay);
        },
        resumeAutoPlay() {
            if (this.timer) clearInterval(this.timer);
            if (this.autoPlayDelay) clearTimeout(this.autoPlayDelay);
            this.autoPlayDelay = setTimeout(() => {
                this.startAutoPlay();
            }, 3000);
        },
        next() {
            if (!this.images.length) return;
            this.current = (this.current + 1) % this.images.length;
        },
        prev() {
            if (!this.images.length) return;
            this.current = (this.current - 1 + this.images.length) % this.images.length;
        },
        goTo(idx) {
            this.current = idx;
        },
        // 触摸事件处理
        handleTouchStart(event) {
            this.touchStartX = event.touches[0].clientX;
            this.touchStartY = event.touches[0].clientY;
            this.isSwiping = false;
            this.isDragging = true;
            this.dragOffset = 0;
            this.pauseAutoPlay(); // 手动滑动时暂停自动切换
        },
        handleTouchMove(event) {
            if (!this.touchStartX || !this.touchStartY) return;

            this.touchEndX = event.touches[0].clientX;
            this.touchEndY = event.touches[0].clientY;

            const deltaX = this.touchEndX - this.touchStartX;
            const deltaY = this.touchEndY - this.touchStartY;

            // 判断是否为水平滑动
            if (Math.abs(deltaX) > Math.abs(deltaY)) {
                this.isSwiping = true;
                this.dragOffset = deltaX;
                event.preventDefault(); // 防止页面滚动
            }
        },
        handleTouchEnd(event) {
            if (!this.isSwiping || !this.touchStartX || !this.touchEndX) {
                this.resetTouchState();
                this.resumeAutoPlay();
                return;
            }

            const deltaX = this.touchEndX - this.touchStartX;
            const minSwipeDistance = 30; // 降低阈值

            if (Math.abs(deltaX) > minSwipeDistance) {
                if (deltaX < 0) {
                    // 向左滑动，显示下一张
                    this.next();
                } else {
                    // 向右滑动，显示上一张
                    this.prev();
                }
            }

            this.resetTouchState();
            this.resumeAutoPlay();
        },
        resetTouchState() {
            this.touchStartX = 0;
            this.touchStartY = 0;
            this.touchEndX = 0;
            this.touchEndY = 0;
            this.isSwiping = false;
            this.isDragging = false;
            this.dragOffset = 0;
        }
    },
    computed: {
        dragStyle() {
            if (this.isDragging && Math.abs(this.dragOffset) > 0) {
                return {
                    transform: `translateX(${this.dragOffset}px)`,
                    transition: 'none'
                };
            } else {
                return {
                    transform: 'translateX(0)',
                    transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)'
                };
            }
        }
    }
};
</script>

<style scoped>
.carousel {
    width: 100%;
    height: 600px;
    background: linear-gradient(120deg, #e0e7ff 0%, #f7f8fa 100%);
    position: relative;
    overflow: hidden;
    margin-bottom: 40px;
    border-radius: 24px;
    box-shadow: 0 8px 32px rgba(30, 60, 120, 0.10);
}

.carousel-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
}

.carousel-item {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 1;
    transform: scale(1);
    z-index: 2;
    transition: opacity 0.8s cubic-bezier(.4, 0, .2, 1), transform 0.8s;
}

.carousel-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 24px;
    box-shadow: 0 4px 24px rgba(30, 60, 120, 0.10);
}

.carousel-caption {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(0, 0, 0, 0.3) 100%);
    color: #fff;
    padding: 24px 32px 32px 32px;
    font-size: 22px;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-radius: 0 0 24px 24px;
    animation: fadeInUp 1s;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(40px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.carousel-title {
    color: #ffffff;
    font-size: 2.4rem;
    font-weight: 800;
    text-decoration: none;
    cursor: pointer;
    margin-bottom: 8px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 20px rgba(0, 0, 0, 0.5);
    letter-spacing: 0.5px;
    line-height: 1.2;
    transition: color 0.2s, text-shadow 0.2s;
    display: inline-block;
}

.carousel-title:hover {
    color: #ffd600;
}

.carousel-detail-btn {
    display: inline-block;
    background: rgba(255, 255, 255, 0.85);
    color: #222;
    border-radius: 28px;
    padding: 10px 36px;
    font-size: 1.08rem;
    font-weight: 600;
    text-decoration: none;
    border: 2px solid #222;
    margin-top: 8px;
    width: fit-content;
    box-shadow: 0 2px 12px rgba(30, 60, 120, 0.06);
    transition: background 0.2s, color 0.2s, box-shadow 0.2s;
    letter-spacing: 1px;
}

.carousel-detail-btn:hover {
    background: #222;
    color: #fff;
    box-shadow: 0 4px 18px rgba(30, 60, 120, 0.13);
}

.carousel-indicators {
    position: absolute;
    left: 50%;
    bottom: 18px;
    transform: translateX(-50%);
    display: flex;
    gap: 12px;
    z-index: 10;
}

.carousel-indicators span {
    display: block;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    opacity: 0.5;
    cursor: pointer;
    transition: background 0.3s, opacity 0.3s, transform 0.3s;
}

.carousel-indicators span.active {
    background: #3b82f6;
    opacity: 1;
    transform: scale(1.2);
}

.carousel-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.3);
    color: #fff;
    border: none;
    font-size: 32px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    z-index: 2;
    box-shadow: 0 2px 8px rgba(30, 60, 120, 0.10);
}

.carousel-btn.prev {
    left: 16px;
}

.carousel-btn.next {
    right: 16px;
}

@media (max-width: 768px) {
    .carousel {
        height: 280px;
        border-radius: 12px;
    }

    .carousel-caption {
        font-size: 15px;
        padding: 12px 8px 16px 8px;
        gap: 8px;
        border-radius: 0 0 12px 12px;
    }

    .carousel-title {
        font-size: 1.3rem;
        font-weight: 700;
        text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8), 0 0 15px rgba(0, 0, 0, 0.5);
    }

    .carousel-detail-btn {
        font-size: 0.98rem;
        padding: 7px 18px;
        border-radius: 18px;
    }

    .carousel-indicators span {
        width: 10px;
        height: 10px;
    }
}
</style>